import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import LanguageSelector from '@/components/LanguageSelector';
import { openCookieSettings } from '@/components/CookieBanner';

const SECTIONS = {
  privacy: [
    ['Responsable del tratamiento', 'RestoSupply (plataforma B2B para hostelería). Contacto RGPD: privacidad@restosupply.eu.'],
    ['Datos tratados', 'Datos de la empresa (nombre, CIF/NIF, nº IVA intracomunitario, país, dirección postal), datos de contacto (email, teléfono), geolocalización aproximada por código postal, historial de pedidos, mensajes de chat y preferencias de consentimiento.'],
    ['Finalidad y base jurídica', 'Ejecución del contrato (art. 6.1.b RGPD): conectar compradores y vendedores, gestionar pedidos y pagos. Obligación legal (art. 6.1.c): facturación e IVA intracomunitario (validación VIES). Consentimiento (art. 6.1.a): analítica y marketing.'],
    ['Conservación', 'Mientras la cuenta esté activa y, después, el plazo exigido por la normativa fiscal de cada Estado miembro (mínimo 4-10 años para documentos contables).'],
    ['Destinatarios y transferencias', 'Proveedores de pago (Stripe), autenticación (Google), alojamiento en la UE. No se realizan transferencias fuera del EEE sin garantías adecuadas (cláusulas contractuales tipo).'],
    ['Tus derechos', 'Acceso, rectificación, supresión, limitación, portabilidad y oposición. Puedes ejercerlos desde Ajustes o escribiendo a privacidad@restosupply.eu. También puedes reclamar ante tu autoridad de control (en España, la AEPD).'],
    ['Marco unificado UE', 'Esta política aplica el Reglamento (UE) 2016/679 (RGPD/GDPR) de forma idéntica en todos los Estados miembros donde opera la plataforma.'],
  ],
  cookies: [
    ['Cookies necesarias', 'Sesión de usuario (token), idioma elegido, preferencias de cookies y carrito local. No requieren consentimiento.'],
    ['Cookies de analítica', 'Solo con tu consentimiento. Métricas de uso agregadas para mejorar la plataforma.'],
    ['Cookies de marketing', 'Solo con tu consentimiento. Comunicaciones comerciales personalizadas.'],
    ['Gestión del consentimiento', 'Tu elección se guarda en tu dispositivo y en tu cuenta (versión de política incluida). Puedes cambiarla en cualquier momento con el botón "Gestionar cookies".'],
  ],
};

const DOC_KEY = { privacy: 'privacy', terms: 'terms' };

const LegalPage = ({ kind }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const lang = (i18n.language || 'es').slice(0, 2);
  useEffect(() => {
    if (!DOC_KEY[kind]) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/legal/docs/${DOC_KEY[kind]}`, { params: { lang } }).then((r) => setDoc(r.data)).catch(() => setDoc(null));
  }, [kind, lang]);
  const sections = DOC_KEY[kind] ? (doc?.sections || []).map((s) => [s.heading, s.paragraphs.join('\n')]) : SECTIONS[kind];
  const title = DOC_KEY[kind] ? (doc?.title || '…') : t(kind === 'privacy' ? 'legal.privacyTitle' : 'legal.cookiesTitle');
  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid={`legal-${kind}`}>
      <div className="max-w-3xl mx-auto bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-center justify-between gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="legal-back">← {t('legal.back')}</Button>
          <LanguageSelector />
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{title}</h1>
        <p className="text-xs text-slate-500 mt-1">{doc?.updated ? `Última actualización: ${doc.updated}` : 'Versión 2026-06'} · Reglamento (UE) 2016/679</p>
        <div className="mt-6 space-y-5">
          {sections.map(([h, body], i) => (
            <section key={i}>
              {h && <h2 className="text-base md:text-lg font-semibold text-slate-900">{h}</h2>}
              <p className="text-sm text-slate-700 mt-1 leading-relaxed whitespace-pre-line">{body}</p>
            </section>
          ))}
        </div>
        {DOC_KEY[kind] && (
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/legal/docs/${DOC_KEY[kind]}/pdf`} target="_blank" rel="noreferrer" className="inline-block mt-6 text-sm text-emerald-700 underline" data-testid="legal-pdf-link">PDF original (ES)</a>
        )}
        {kind === 'privacy' && (
          <p className="mt-6 text-sm"><Link to="/eliminar-cuenta" className="text-red-700 underline" data-testid="legal-delete-account-link">{t('delAcc.link')}</Link></p>
        )}
        {kind === 'cookies' && (
          <Button className="mt-6 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={openCookieSettings} data-testid="legal-manage-cookies">{t('cookies.manage')}</Button>
        )}
      </div>
    </div>
  );
};

export default LegalPage;
